"use client";

import { c as _c } from "react/compiler-runtime";
;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:propstrata-app": true
    });
  } catch (e) {}
}();
import { Clock, Database, Loader2, Pencil, PlusCircle, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";
import { CreateDataProvider, controlCreateDataProvider, controlDeleteDataProvider, controlEditDataProvider, controlFinishGoToConnectSetup, controlFinishPodiumOrganizationSetup, DeleteDataProvider, EditDataProvider, FinishGoToConnectSetup, FinishPodiumOrganizationSetup } from "@/components/settings/data-providers";
import { NON_EDITABLE_PROVIDERS, PROVIDER_NAMES } from "@/components/settings/data-providers/constants";
import { PodiumScopeUpgradeAccess } from "@/components/settings/data-providers/podium-scope-upgrade-access";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Separator } from "@/components/ui/separator";
import { ProviderIcon } from "@/features/etl-automation/components/shared/provider-icon";
import { TOUR_TARGETS } from "@/features/onboarding-tours/orchestrator/tour-target-ids";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { useDataProviders } from "@/hooks/useDataProviders";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils/date";
import { hasWorkspacePermission } from "@/lib/utils/workspace-permissions";
import type { DataProvider } from "@/queries/data-provider/fetchers";
import { retryPodiumWebhookInstallation } from "@/queries/data-provider/updates";
import { ProviderType } from "@/types/dataProvider";
const getProviderConnectedAt = (provider: DataProvider): string | Date => {
  const credential: any = provider.credentials?.[0];
  return credential?.updatedAt ?? credential?.createdAt ?? provider.createdAt;
};
type PodiumCredentialSnapshot = {
  isPending?: boolean;
  organizationConnections?: {
    organizationName: string;
    organizationUid: string;
  }[];
  organizationChoices?: {
    organizationName: string;
    organizationUid: string;
  }[];
  organizationName?: string;
  organizationUid?: string;
  scope?: string[];
};
type PodiumWebhookSnapshot = {
  isActive?: boolean;
  resourceId?: string | null;
  resourceName?: string | null;
  status?: "ACTIVE" | "FAILED" | "INACTIVE" | "PENDING";
};
const PODIUM_READ_MESSAGES_SCOPE = "read_messages";
function hasPodiumReadMessagesScope(scope: string[] | undefined): boolean {
  return Array.isArray(scope) && scope.includes(PODIUM_READ_MESSAGES_SCOPE);
}
function getPodiumConnectionState(provider: DataProvider) {
  const credentials = provider.credentials?.[0]?.credentials as PodiumCredentialSnapshot | undefined;
  const organizationConnections = credentials?.organizationConnections ?? (credentials?.organizationUid && credentials.organizationName ? [{
    organizationName: credentials.organizationName,
    organizationUid: credentials.organizationUid
  }] : []);
  const webhooks = (provider.webhooks ?? []) as PodiumWebhookSnapshot[];
  const activeOrganizationCount = organizationConnections.filter(organizationConnection => webhooks.some(webhook => webhook.resourceId === organizationConnection.organizationUid && webhook.isActive === true && webhook.status === "ACTIVE")).length;
  const hasFailedOrganization = organizationConnections.some(organizationConnection => webhooks.some(webhook => webhook.resourceId === organizationConnection.organizationUid && (webhook.status === "FAILED" || webhook.status === "PENDING")));
  const hasReadMessagesScope = hasPodiumReadMessagesScope(credentials?.scope);
  if (credentials?.isPending) {
    return {
      badgeLabel: "Setup incomplete",
      isRetryable: false,
      isScopeUpgradeRequired: false,
      isWarning: true,
      organizationCount: 0
    };
  }
  if (activeOrganizationCount > 0) {
    if (!hasReadMessagesScope) {
      return {
        badgeLabel: "Podium Scope Upgrade Required",
        isRetryable: false,
        isScopeUpgradeRequired: true,
        isWarning: true,
        organizationCount: activeOrganizationCount
      };
    }
    return {
      badgeLabel: `${activeOrganizationCount} organization${activeOrganizationCount === 1 ? "" : "s"} connected`,
      isRetryable: hasFailedOrganization,
      isScopeUpgradeRequired: false,
      isWarning: hasFailedOrganization,
      organizationCount: activeOrganizationCount
    };
  }
  if (organizationConnections.length > 0) {
    return {
      badgeLabel: "Setup failed with Retry",
      isRetryable: true,
      isScopeUpgradeRequired: false,
      isWarning: true,
      organizationCount: 0
    };
  }
  return {
    badgeLabel: "Reconnect Required",
    isRetryable: false,
    isScopeUpgradeRequired: false,
    isWarning: true,
    organizationCount: 0
  };
}
export function DataProviders() {
  const $ = _c(61);
  const {
    workspaceId,
    workspace
  } = useCurrentWorkspace();
  const {
    dataProviders: t0,
    loading,
    error,
    refreshProviders,
    deletedProviderId,
    deletingWorkspaceDataProvider: isDeleting
  } = useDataProviders();
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dataProviders = t1;
  const permissions = workspace?.permissions;
  let t2;
  if ($[2] !== permissions) {
    t2 = hasWorkspacePermission(permissions, "dataProvider", "canCreate");
    $[2] = permissions;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const canCreateFromPermission = t2;
  let t3;
  if ($[4] !== canCreateFromPermission || $[5] !== dataProviders) {
    t3 = canCreateFromPermission && dataProviders.length < Object.keys(ProviderType).length;
    $[4] = canCreateFromPermission;
    $[5] = dataProviders;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const canCreateDataProviders = t3;
  let t4;
  if ($[7] !== permissions) {
    t4 = hasWorkspacePermission(permissions, "dataProvider", "canEdit");
    $[7] = permissions;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const canEditDataProviders = t4;
  let t5;
  if ($[9] !== permissions) {
    t5 = hasWorkspacePermission(permissions, "dataProvider", "canDelete");
    $[9] = permissions;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const canDeleteDataProviders = t5;
  const canViewDataProviders = hasWorkspacePermission(permissions, "dataProvider", "*");
  let t6;
  bb0: {
    if (loading) {
      let t7;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <LoadingSpinner className="min-h-50" />;
        $[11] = t7;
      } else {
        t7 = $[11];
      }
      t6 = t7;
      break bb0;
    }
    if (error) {
      let t7;
      if ($[12] !== refreshProviders) {
        t7 = () => {
          refreshProviders();
        };
        $[12] = refreshProviders;
        $[13] = t7;
      } else {
        t7 = $[13];
      }
      const t8 = loading ? "Fetching..." : "Retry";
      let t9;
      if ($[14] !== loading || $[15] !== t7 || $[16] !== t8) {
        t9 = <EmptyState className="w-full min-h-50" description="Failed to load data providers" icon={Database} title="Error"><Button disabled={loading} onClick={t7} type="button" variant="default">{t8}</Button></EmptyState>;
        $[14] = loading;
        $[15] = t7;
        $[16] = t8;
        $[17] = t9;
      } else {
        t9 = $[17];
      }
      t6 = t9;
      break bb0;
    }
    if (dataProviders.length === 0) {
      let t7;
      if ($[18] !== canCreateDataProviders) {
        t7 = canCreateDataProviders && <Button onClick={_temp} variant="default"><PlusCircle />Add Data Provider</Button>;
        $[18] = canCreateDataProviders;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      let t8;
      if ($[20] !== t7) {
        t8 = <EmptyState className="w-full min-h-50" description="Connect your first data provider to get started" icon={Database} title="No data providers connected">{t7}</EmptyState>;
        $[20] = t7;
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      t6 = t8;
      break bb0;
    }
    let t7;
    if ($[22] !== canDeleteDataProviders || $[23] !== canEditDataProviders || $[24] !== dataProviders || $[25] !== deletedProviderId || $[26] !== isDeleting || $[27] !== refreshProviders || $[28] !== workspaceId) {
      let t8;
      if ($[30] !== canDeleteDataProviders || $[31] !== canEditDataProviders || $[32] !== dataProviders[0]?.id || $[33] !== deletedProviderId || $[34] !== isDeleting || $[35] !== refreshProviders || $[36] !== workspaceId) {
        t8 = provider => {
          const providerCredentials = provider.credentials?.[0]?.credentials as {
            accountChoices?: {
              accountKey: string;
              accountName: string;
            }[];
            isPending?: boolean;
            organizationChoices?: {
              organizationName: string;
              organizationUid: string;
            }[];
            organizationConnections?: {
              organizationName: string;
              organizationUid: string;
            }[];
            organizationName?: string;
            organizationUid?: string;
            reconnectRequired?: boolean;
            refreshTokenExpiresAt?: number;
            scope?: string[];
          } | undefined;
          const podiumConnectionState = provider.type === ProviderType.PODIUM ? getPodiumConnectionState(provider) : null;
          const isEditable = !NON_EDITABLE_PROVIDERS.includes(provider.type as ProviderType);
          const isGoToPending = provider.type === ProviderType.GOTO_CONNECT && Boolean(providerCredentials?.isPending);
          const isGoToReconnectRequired = provider.type === ProviderType.GOTO_CONNECT && !isGoToPending && providerCredentials?.reconnectRequired === true;
          const isPodiumPending = provider.type === ProviderType.PODIUM && Boolean(providerCredentials?.isPending);
          const isPodiumReconnectRequired = provider.type === ProviderType.PODIUM && !providerCredentials?.isPending && podiumConnectionState?.badgeLabel === "Reconnect Required";
          const isPodiumScopeUpgradeRequired = provider.type === ProviderType.PODIUM && podiumConnectionState?.isScopeUpgradeRequired === true;
          const pendingAccountChoices = providerCredentials?.accountChoices ?? [];
          const pendingOrganizationChoices = providerCredentials?.organizationChoices ?? [];
          const isPodiumSetupFailed = provider.type === ProviderType.PODIUM && podiumConnectionState?.isRetryable === true;
          return <div className="group @container/provider-card relative flex flex-col overflow-hidden rounded-lg border" id={dataProviders[0]?.id === provider.id ? TOUR_TARGETS.dataProviderCard : undefined} key={provider.id}><div className="from-background/40 bg-linear-to-br via-muted to-sky-50 px-1 relative"><div className="flex flex-col items-start gap-1 px-1 pt-1 sm:flex-row sm:justify-between"><Badge className={isGoToPending || isGoToReconnectRequired || isPodiumPending || isPodiumReconnectRequired || podiumConnectionState?.isWarning ? "border border-amber-500/30 text-amber-700 bg-amber-500/10 text-[10px]" : "text-[10px] border border-border"} variant={isGoToPending || isGoToReconnectRequired || isPodiumPending || isPodiumReconnectRequired || podiumConnectionState?.isWarning ? "outline" : "secondary"}><span className={`inline-block size-1.5 rounded-full ${isGoToPending || isGoToReconnectRequired || isPodiumPending || isPodiumReconnectRequired || podiumConnectionState?.isWarning ? "bg-amber-600" : "bg-emerald-700"}`} /><span>{isGoToPending || isPodiumPending ? "Setup incomplete" : isGoToReconnectRequired || isPodiumReconnectRequired ? "Reconnect Required" : podiumConnectionState?.badgeLabel ?? "Connected"}</span></Badge><div className={cn("flex self-end gap-1.5 sm:self-auto transition-opacity", dataProviders[0]?.id === provider.id ? "opacity-100" : "xl:opacity-0 group-hover:opacity-100")}>{canEditDataProviders && isGoToPending && <Button className="hidden h-6 border-border bg-transparent px-2 text-xs shadow-none hover:bg-white @[18rem]/provider-card:inline-flex" onClick={() => {
                    controlFinishGoToConnectSetup({
                      data: {
                        providerId: provider.id
                      },
                      state: "on"
                    });
                  }} variant="ghost">Finish setup</Button>}{canEditDataProviders && provider.type === ProviderType.PODIUM && isPodiumPending && <Button className="hidden h-6 border-border bg-transparent px-2 text-xs shadow-none hover:bg-white @[18rem]/provider-card:inline-flex" onClick={() => {
                    controlFinishPodiumOrganizationSetup({
                      data: {
                        providerId: provider.id
                      },
                      state: "on"
                    });
                  }} variant="ghost">Finish setup</Button>}{canEditDataProviders && provider.type === ProviderType.PODIUM && isPodiumScopeUpgradeRequired && <PodiumScopeUpgradeAccess workspaceId={workspaceId ?? ""} />}{canEditDataProviders && isPodiumSetupFailed && <Button className="bg-transparent hover:bg-white shadow-none border-border h-6 px-2 text-xs" onClick={async () => {
                    if (!workspaceId) {
                      return;
                    }
                    try {
                      await retryPodiumWebhookInstallation({
                        workspaceId
                      });
                      await refreshProviders();
                      toast.success("Podium setup retried successfully");
                    } catch {
                      toast.error("Failed to retry Podium setup");
                    }
                  }} variant="ghost">Retry</Button>}{canEditDataProviders && isEditable && <Button className="bg-transparent hover:bg-white shadow-none border-border size-6 [&_svg]:size-4! xl:[&_svg]:size-4! xl:size-6" onClick={() => {
                    controlEditDataProvider({
                      data: {
                        providerId: provider.id
                      },
                      state: "on"
                    });
                  }} size="icon" variant="ghost"><Pencil strokeWidth={1.5} /></Button>}{canDeleteDataProviders && <Button className="bg-transparent hover:bg-white size-6 [&_svg]:size-4! xl:[&_svg]:size-4! xl:size-6" id={dataProviders[0]?.id === provider.id ? TOUR_TARGETS.dataProviderDelete : undefined} onClick={() => {
                    controlDeleteDataProvider({
                      data: {
                        providerId: provider.id
                      },
                      state: "on"
                    });
                  }} size="icon" variant="ghost">{isDeleting && deletedProviderId === provider.id ? <Loader2 className="animate-spin" strokeWidth={1.5} /> : <Trash2 className="text-destructive" strokeWidth={1.5} />}</Button>}</div></div><div className="flex flex-row gap-2 items-center px-2 py-3"><div className="size-12 shrink-0 rounded-lg border bg-card/80 flex items-center justify-center"><ProviderIcon className="max-h-8 max-w-8" providerId={provider.type} size={32} /></div><p className="text-sm font-medium">{PROVIDER_NAMES[provider.type]}</p></div>{isGoToPending && pendingAccountChoices.length > 0 && <p className="px-2 text-xs text-amber-700">{pendingAccountChoices.length} account{pendingAccountChoices.length === 1 ? "" : "s"} available to finish setup</p>}{isPodiumPending && <p className="mt-1 px-2 pb-3 text-xs text-amber-700">{pendingOrganizationChoices.length > 0 ? `${pendingOrganizationChoices.length} organization${pendingOrganizationChoices.length === 1 ? "" : "s"} discovered during setup` : "No accessible organizations were discovered during setup"}</p>}{isPodiumScopeUpgradeRequired && <p className="mt-1 px-2 pb-3 text-xs text-amber-700">Podium is still collecting call history, but call conversation enrichment needs read_messages.</p>}</div><Separator /><div className="flex flex-row items-center flex-1 p-2 gap-1 text-muted-foreground"><Clock className="size-3" /><p className="text-[10px]">{formatDate(getProviderConnectedAt(provider))}</p></div></div>;
        };
        $[30] = canDeleteDataProviders;
        $[31] = canEditDataProviders;
        $[32] = dataProviders[0]?.id;
        $[33] = deletedProviderId;
        $[34] = isDeleting;
        $[35] = refreshProviders;
        $[36] = workspaceId;
        $[37] = t8;
      } else {
        t8 = $[37];
      }
      t7 = dataProviders.map(t8);
      $[22] = canDeleteDataProviders;
      $[23] = canEditDataProviders;
      $[24] = dataProviders;
      $[25] = deletedProviderId;
      $[26] = isDeleting;
      $[27] = refreshProviders;
      $[28] = workspaceId;
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    let t8;
    if ($[38] !== t7) {
      t8 = <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-2">{t7}</div>;
      $[38] = t7;
      $[39] = t8;
    } else {
      t8 = $[39];
    }
    t6 = t8;
  }
  const contentToRender = t6;
  if (!workspaceId || !canViewDataProviders) {
    return null;
  }
  let t7;
  let t8;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CardTitle>Data Providers</CardTitle>;
    t8 = <Separator className="flex-1" />;
    $[40] = t7;
    $[41] = t8;
  } else {
    t7 = $[40];
    t8 = $[41];
  }
  let t9;
  if ($[42] !== canCreateDataProviders) {
    t9 = canCreateDataProviders && <Button className="bg-secondary/70 border hover:bg-secondary duration-200 transition-colors" id={TOUR_TARGETS.addDataProviderButton} onClick={_temp2} size="sm" variant="ghost"><PlusCircle />Add Data Provider</Button>;
    $[42] = canCreateDataProviders;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] !== t9) {
    t10 = <div className="flex items-center gap-4">{t7}{t8}{t9}</div>;
    $[44] = t9;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  let t11;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <CardDescription>Connected data sources and integrations</CardDescription>;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== t10) {
    t12 = <CardHeader className="px-2 sm:px-4 gap-0.5">{t10}{t11}</CardHeader>;
    $[47] = t10;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== contentToRender) {
    t13 = <CardContent className="px-2 sm:px-12">{contentToRender}</CardContent>;
    $[49] = contentToRender;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== t12 || $[52] !== t13) {
    t14 = <Card className="shadow-none bg-transparent sm:gap-y-8 border-none" id={TOUR_TARGETS.dataProvidersSection}>{t12}{t13}</Card>;
    $[51] = t12;
    $[52] = t13;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DeleteDataProvider />;
    t16 = <EditDataProvider />;
    t17 = <FinishGoToConnectSetup />;
    t18 = <FinishPodiumOrganizationSetup />;
    t19 = <CreateDataProvider />;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
    $[58] = t19;
  } else {
    t15 = $[54];
    t16 = $[55];
    t17 = $[56];
    t18 = $[57];
    t19 = $[58];
  }
  let t20;
  if ($[59] !== t14) {
    t20 = <>{t14}{t15}{t16}{t17}{t18}{t19}</>;
    $[59] = t14;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  return t20;
}
function _temp2() {
  controlCreateDataProvider({
    state: "on"
  });
}
function _temp() {
  controlCreateDataProvider({
    state: "on"
  });
}